import { type FC, Fragment, useState } from "react";
import { useDispatch } from "react-redux";
import { hideDialog } from "../../Actions/DialogActions";
import Button from "../../Common/Button";
import Input from "../../Common/Input/Input";

interface Props {
    client?: string;
    name?: string;
    redirectUri?: string;
    embedded?: string;
    onSubmit: (name: string, redirectUri: string, clientId: string, embedded: string) => void;
}

const ApplicationCreate: FC<Props> = ({
    client,
    name: initialName,
    redirectUri: initialRedirectUri,
    embedded: initialEmbedded,
    onSubmit,
}) => {
    const dispatch = useDispatch();
    const [ name, setName ] = useState(initialName || "");
    const [ redirectUri, setRedirectUri ] = useState(initialRedirectUri || "");
    const [ embedded, setEmbedded ] = useState(initialEmbedded || "");
    // Tracked but not currently surfaced anywhere, matching the previous class component's
    // behaviour - setError is still called on validation failure below.
    const [ , setError ] = useState("");

    const onCancel = (): void => {
        dispatch(hideDialog());
    };

    const onConfirm = (): void => {
        if(!name) {
            setError("Invalid Name");

            return;
        }

        if(!redirectUri) {
            setError("Invalid Redirect URI");

            return;
        }

        dispatch(hideDialog());

        onSubmit(name, redirectUri, client || "", embedded);
    };

    return (
        <Fragment>
            <p className="mb-4 text-[var(--color-text-secondary)]">
                An application is how you connect to Shopfront stores using OAuth 2.0 - once a
                store owner approves it, you can make API calls on their behalf. The details
                below are just to get you a Client ID and secret; you can change them later.
            </p>
            <div className="mb-4">
                <Input
                    icon="tag"
                    label="Name"
                    type="text"
                    name="name"
                    value={name}
                    onChange={e => setName(e.currentTarget.value)}
                    hint="Shown to store owners when they're asked to approve your application."
                />
            </div>
            <div className="mb-4">
                <Input
                    icon="link"
                    label="Redirect URI"
                    type="text"
                    name="redirectUri"
                    value={redirectUri}
                    onChange={e => setRedirectUri(e.currentTarget.value)}
                    hint={
                        "Where a store is sent back to after approving your application, with an " +
                        "authorization code appended for you to exchange for an access token."
                    }
                />
            </div>
            {typeof initialEmbedded !== "undefined" && (
                <div className="mb-4">
                    <Input
                        icon="anchor"
                        label="Embedded API URI"
                        type="text"
                        name="embedded"
                        value={embedded}
                        onChange={e => setEmbedded(e.currentTarget.value)}
                        hint={
                            "Only needed if your application embeds directly into Shopfront - the " +
                            "URI we'll load inside it."
                        }
                    />
                </div>
            )}
            <div className="dialog-buttons">
                <Button
                    type="button"
                    classes={[ "secondary" ]}
                    onClick={onCancel}
                >
                    Cancel
                </Button>
                <Button
                    type="button"
                    classes={[ "primary" ]}
                    onClick={onConfirm}
                >
                    Confirm
                </Button>
            </div>
        </Fragment>
    );
};

export default ApplicationCreate;
